import { useEffect, useState } from "react";
import { Plus, Trash2, Calendar, X } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

export default function RecurringPanel({ project_slug }) {
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [cadence, setCadence] = useState("daily");
  const [dow, setDow] = useState([1, 2, 3, 4, 5]); // weekdays
  const [adding, setAdding] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const reload = () => adminApi.listRecurring(project_slug).then((r) => setList(r.data));

  useEffect(() => {
    reload();
  }, [project_slug]);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setAdding(true);
    try {
      await adminApi.createRecurring({
        project_slug,
        text: text.trim(),
        cadence,
        days_of_week: cadence === "weekly" ? dow : undefined,
      });
      setText("");
      reload();
      toast.success("Habit added");
    } catch (e) {
      toast.error(e.message || "Failed");
    } finally {
      setAdding(false);
    }
  };

  const onDelete = async (id) => {
    await adminApi.deleteRecurring(id);
    reload();
  };

  return (
    <div data-testid="recurring-panel">
      <p className="text-xs tracking-widest opacity-60 mb-3 text-four-yellow flex items-center gap-2">
        <Calendar size={12} /> RECURRING HABITS
      </p>

      <ul className="space-y-2 mb-4">
        {list.length === 0 && <li className="text-xs opacity-30 italic">no habits yet</li>}
        {list.map((r) => (
          <li
            key={r.id}
            className="flex items-center gap-3 py-1"
            data-testid={`recurring-item-${r.id}`}
          >
            <span className="flex-1 text-sm">{r.text}</span>
            <span className="text-[10px] tracking-widest opacity-50">
              {r.cadence === "daily"
                ? "DAILY"
                : `${(r.days_of_week || []).map((d) => DAY_LABELS[d]).join("")}`}
            </span>
            {confirmId === r.id ? (
              <>
                <button
                  onClick={() => {
                    onDelete(r.id);
                    setConfirmId(null);
                  }}
                  className="text-xs tracking-widest text-red-400 hover:text-red-300"
                  data-testid={`recurring-delete-confirm-${r.id}`}
                >
                  DELETE?
                </button>
                <button
                  onClick={() => setConfirmId(null)}
                  className="opacity-60 hover:opacity-100"
                  aria-label="cancel delete"
                >
                  <X size={14} />
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmId(r.id)}
                className="opacity-50 hover:opacity-100 hover:text-red-400 transition-colors"
                data-testid={`recurring-delete-${r.id}`}
                aria-label="delete habit"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>

      <form onSubmit={onAdd} className="space-y-2 border-t border-parchment-faint pt-3">
        <div className="flex items-center gap-2">
          <Plus size={14} className="opacity-40" />
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="new habit…"
            disabled={adding}
            className="flex-1 bg-transparent border-b border-parchment-faint focus:border-four-yellow outline-none text-sm py-1 transition-colors placeholder:opacity-30"
            data-testid="recurring-add-input"
          />
        </div>
        <div className="flex items-center gap-3 text-xs">
          <select
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
            className="bg-ink border border-parchment-faint px-2 py-1 outline-none"
            data-testid="recurring-cadence"
          >
            <option value="daily">Every day</option>
            <option value="weekly">Specific days</option>
          </select>
          {cadence === "weekly" && (
            <div className="flex gap-1">
              {DAY_LABELS.map((lbl, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() =>
                    setDow((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]))
                  }
                  className={`w-6 h-6 text-[10px] border ${
                    dow.includes(i)
                      ? "border-four-yellow text-four-yellow"
                      : "border-parchment-faint opacity-60"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          )}
          <button
            type="submit"
            disabled={adding || !text.trim()}
            className="ml-auto px-3 py-1 border border-parchment-faint hover:border-four-yellow hover:text-four-yellow tracking-widest disabled:opacity-30 transition-colors"
            data-testid="recurring-add-submit"
          >
            ADD
          </button>
        </div>
      </form>
    </div>
  );
}
